import React from 'react';
import { Link } from 'react-router-dom';
import { Copyright, ChevronLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const CopyrightPolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00C0FF] hover:text-white transition-colors mb-6">
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[#0066CC]/20 rounded-xl flex items-center justify-center">
              <Copyright className="w-6 h-6 text-[#00C0FF]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Copyright & IP Policy</h1>
          </div>
          <p className="text-gray-400">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">1. Copyright Ownership</h2>
            <p className="text-gray-600 mb-6">
              All content on this Website, including but not limited to text, graphics, logos, images, videos, icons, software, and the compilation thereof, is the property of SRAGCircle OPC or its content suppliers and is protected by Philippine and international copyright laws.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">2. Trademarks</h2>
            <p className="text-gray-600 mb-4">The following are trademarks or service marks of SRAGCircle OPC:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>SRAGCircle OPC name and logo</li>
              <li>"Systems, Resources & Automations Group" tagline</li>
              <li>LexDoRight name and logo</li>
              <li>Any other product or service names displayed on this Website</li>
            </ul>
            <p className="text-gray-600 mb-6">
              These marks may not be used without the prior written permission of SRAGCircle OPC.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">3. Permitted Use</h2>
            <p className="text-gray-600 mb-4">You may:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>View and browse the Website for personal, non-commercial purposes</li>
              <li>Print or download content for personal reference only</li>
              <li>Share links to our Website pages</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">4. Prohibited Use</h2>
            <p className="text-gray-600 mb-4">Without our express written consent, you may not:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Reproduce, duplicate, copy, sell, or exploit any portion of the Website for commercial purposes</li>
              <li>Modify, adapt, or create derivative works from our content</li>
              <li>Remove any copyright, trademark, or proprietary notices</li>
              <li>Use our trademarks in any manner that implies endorsement or affiliation</li>
              <li>Frame or mirror any part of our Website</li>
              <li>Use automated systems (bots, scrapers) to extract content</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">5. Intellectual Property in Services</h2>
            <p className="text-gray-600 mb-6">
              Any intellectual property created by SRAGCircle OPC in the course of providing services to clients, including system designs, software, documentation, and technical solutions, shall remain the property of SRAGCircle OPC unless otherwise agreed in writing through a separate contract.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">6. Third-Party Content</h2>
            <p className="text-gray-600 mb-6">
              Certain content on our Website may be licensed from third parties. Such content remains the property of the respective owners and is used in accordance with applicable licenses. Third-party trademarks displayed on our Website are the property of their respective owners.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">7. User Submissions</h2>
            <p className="text-gray-600 mb-6">
              By submitting any content to us through our contact form or other means, you grant SRAGCircle OPC a non-exclusive, royalty-free right to use, reproduce, and process such content for the purpose of responding to your inquiry and providing our services.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">8. Copyright Infringement Claims</h2>
            <p className="text-gray-600 mb-4">
              If you believe that any content on our Website infringes upon your copyright, please notify us immediately with the following information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>A description of the copyrighted work you claim has been infringed</li>
              <li>The location (URL) of the allegedly infringing material on our Website</li>
              <li>Your contact information (name, address, email, phone number)</li>
              <li>A statement that you have a good faith belief that the use is not authorized</li>
              <li>A statement, under penalty of perjury, that the information is accurate and that you are authorized to act on behalf of the copyright owner</li>
              <li>Your physical or electronic signature</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">9. Enforcement</h2>
            <p className="text-gray-600 mb-6">
              SRAGCircle OPC reserves the right to take legal action against any unauthorized use of our intellectual property. Violations may result in civil and criminal penalties under the Intellectual Property Code of the Philippines (Republic Act No. 8293) and other applicable laws.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">10. Licensing Inquiries</h2>
            <p className="text-gray-600 mb-6">
              For inquiries regarding licensing our content, trademarks, or other intellectual property, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-gray-700 font-semibold">SRAGCircle OPC - Legal Department</p>
              <p className="text-gray-600">Email: contact@sragcircle.com</p>
              <p className="text-gray-600">Phone: (63) 926 036 6898</p>
              <p className="text-gray-600">Location: Metro Manila, Philippines</p>
            </div>

            <div className="mt-10 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-[#1a1a5c] font-semibold mb-2">Notice</p>
              <p className="text-gray-600 text-sm">
                © {new Date().getFullYear()} SRAGCircle OPC. All rights reserved. The content, design, and intellectual property of this Website are protected under Philippine and international laws.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CopyrightPolicy;
